// Centralized DOCM brand & content constants
// Logo is self-hosted on the backend (resilient to upstream WP outages).
// Falls back to a stable Cloudinary CDN URL if REACT_APP_BACKEND_URL is not set.
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const DOCM_LOGO = BACKEND
  ? `${BACKEND}/api/uploads/brand/docm-logo.png`
  : "https://res.cloudinary.com/dn1fk8h6o/image/upload/v1781098248/Frame_16_1_pik9rc.png";
// Mark-only (square seal — no wordmark). Used inside small square containers like
// the 9-Principles wheel medallion and the certificate top crest.
export const DOCM_MARK = BACKEND
  ? `${BACKEND}/api/uploads/brand/docm-mark.png`
  : "https://res.cloudinary.com/dn1fk8h6o/image/upload/v1781098248/Frame_16_1_pik9rc.png";

// Primary navigation — top-level items.
// "About" is the merged Story + 9 Principles page. Contribute lives inside
// Get Involved (#contribute) per the July 2026 movement restructure —
// "Sign the Declaration" stays a right-aligned CTA button. "Our Founder"
// was promoted from the footer into the main menu (Tiger, 2026-07-14).
// 2026-07-21 · Tiger asked to consolidate the three action items ("Do a
// Random Act", "Be a Global Ambassador", "Become The Partner") into a
// single dropdown menu titled "Join The Movement". Individual entries are
// listed under NAV_SUBMENUS.join for the dropdown children.
export const NAV_PRIMARY = [
  { label: "Home", href: "/", type: "route", noWrap: true },
  { label: "About", href: "/about", type: "route", noWrap: true },
  { label: "11·11", href: "/1111", type: "route", highlight: true, noWrap: true, badge: "Global Event", stackedBadge: true },
  { label: "Random Acts of Consciousness", displayLabel: "Random Acts", href: "/random-acts", type: "route", noWrap: true },
  { label: "DOCM Directory", href: "/directory", type: "route", noWrap: true },
  { label: "Join The Movement", hasSubmenu: "join", type: "menu", noWrap: true },
  { label: "Our Founder", href: "/our-founder", type: "route", noWrap: true },
];

// Submenus (keyed by NAV_PRIMARY item's `key`) — Navbar reads NAV_SUBMENUS[item.key]
// to render dropdown children when item.type === "menu".
// 2026-07-26 · Sathish's Proposed Changes doc, items #12 & #19 — "Join The
// Movement" is reduced to exactly TWO options. Clicking either takes the
// visitor to the corresponding create-flow (individual Act OR community
// Event). On successful submission the backend auto-tags them as an
// Ambassador (for Acts) or Partner (for Events).
export const NAV_SUBMENUS = {
  join: [
    { label: "Become a Global Ambassador", href: "/random-acts?path=individual#rac-form", type: "route", desc: "Perform and share a Conscious Act. On submission you become a Global Ambassador." },
    { label: "Become a Partner",           href: "/random-acts?path=community#rac-form",  type: "route", desc: "Create and publish a Conscious Event. On submission you become a Partner." },
  ],
};

// Movement categories — broad thematic buckets for filtering Conscious Acts
// (curated, community-submitted random acts, and Community events). The
// backend's `_category_for_movement()` derives the same labels from curated
// movement strings; this list is what the user sees in pickers and chips.
// "Other" allows free-text customization which is stored as `custom_category`.
export const ACT_CATEGORIES = [
  "Civil Rights",
  "Conservation",
  "Spiritual",
  "Humanitarian",
  "Science",
  "Arts",
  "Peace & Resistance",
  "Social Enterprise",
  "Other",
];

// 2026-07-14 · The six Universal Pillars were retired in favour of the
// 9 Principles (the movement's constitution). The `SUMMIT_PILLARS` export
// used to live here and drove `/1111 Pillars` + `?pillar=<id>` deep-links.
// Removed entirely: /1111 now renders `Principles0808` grounded in the
// same 9 Principles as `/about`, and any legacy `?pillar=<id>` query
// params are silently ignored. Backend keeps its PILLARS_BY_ID for
// historical signatures so old certificate emails don't break.

// The nine commitments of the full Declaration of Consciousness pledge —
// shown to signers in the SignDeclaration form so they see what they're committing to.
export const DECLARATION_COMMITMENTS = [
  "Choosing non-violence, compassion, and respectful dialogue.",
  "Honouring the equal worth, integrity, and rights of every human being.",
  "Respecting freedom of thought, speech, and expression — acting responsibly.",
  "Supporting the safety, equality, and empowerment of women.",
  "Protecting and nurturing the wellbeing, development, and future of children.",
  "Respecting and valuing the wisdom, integrity, and contributions of elders.",
  "Acting consciously in every community I serve.",
  "Caring for Mother Earth through sustainable choices and collective responsibility.",
  "Embracing our shared humanity — building unity across cultures, beliefs, and backgrounds.",
];

// Names-only version of the 9 Principles, used by the decorative wheel on the
// home page. Each entry is a short label + a one-line tagline + accent colour.
// Full essays live on /about#principles in PRINCIPLES_FULL.
export const PRINCIPLE_NAMES = [
  {
    num: "01",
    label: "Ahimsa",
    tagline: "Non-violence & Animal Welfare",
    description: "In realizing our oneness with all life — human and animal alike — we grow compassionate, kind, and respectful toward every living being. Peace is built one practical moment at a time.",
    accent: "moss",
  },
  {
    num: "02",
    label: "Women's Rights",
    tagline: "Equality, safety & opportunity",
    description: "Equal pay, safe spaces, leadership at every table — every woman and girl deserves respect and full participation.",
    accent: "terracotta",
  },
  {
    num: "03",
    label: "Children",
    tagline: "Welfare & wellbeing",
    description: "Safe schools, fair access to learning, protection from harm — childhood is the foundation of every nation.",
    accent: "gold",
  },
  {
    num: "04",
    label: "Elders",
    tagline: "Honouring our elders",
    description: "Honoring our elders is a conscious act of respect, gratitude, and inclusion.",
    accent: "moss",
  },
  {
    num: "05",
    label: "Equality",
    tagline: "Inclusivity, equity & righteousness",
    description: "Every human carries equal worth. Inclusion and right action are the proof of our shared humanity.",
    accent: "terracotta",
  },
  {
    num: "06",
    label: "Freedom",
    tagline: "Of thought, speech & expression",
    description: "Every conscious action begins with the freedom to think independently.",
    accent: "gold",
  },
  {
    num: "07",
    label: "Conscious Orgs",
    tagline: "Conscious & responsible institutions",
    description: "Companies, schools and governments that put consciousness into every decision they make.",
    accent: "moss",
  },
  {
    num: "08",
    label: "Mother Earth",
    tagline: "One planet, one responsibility",
    description: "We are stewards, not owners. Every choice shapes the world we leave behind.",
    accent: "terracotta",
  },
  {
    num: "09",
    label: "Unity for All",
    tagline: "One humanity, many voices",
    description: "Different languages, one heartbeat. We honour diversity and celebrate our shared origin.",
    accent: "gold",
  },
];

// 24-hour rolling format
export const SUMMIT_WAVES = [
  {
    name: "Eastern Wave",
    window: "07:00 – 19:00 IST",
    regions: "India · Sri Lanka · Nepal · Singapore · Japan · Australia",
  },
  {
    name: "Western Wave",
    window: "07:00 – 19:00 PT",
    regions: "Europe · Africa · UK · USA · Latin America",
  },
];

// Contribute tiers (renamed from Donate per user request)
export const CONTRIBUTE_TIERS = [
  {
    label: "Video",
    title: "Share a Video Message",
    body: "Record a short video sharing your message for humanity, your Random Act of Consciousness, or your invitation for others to join on 11·11.",
    cta: "Submit Your Video",
  },
  {
    label: "Knowledge",
    title: "Contribute Knowledge",
    body: "Share articles, research, stories, ideas, local initiatives, community resources — anything that helps the movement grow stronger.",
    cta: "Share Information",
  },
  {
    label: "Amplify",
    title: "Amplify the Message",
    body: "Write an article, share the campaign, invite your community, or help others understand why 11·11 matters.",
    cta: "Amplify 11·11",
  },
];

// Country list reused across forms & map
export const COUNTRIES_LIST = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia","Austria","Azerbaijan",
  "Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia",
  "Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cambodia","Cameroon","Canada",
  "Cape Verde","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia",
  "Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia",
  "Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti",
  "Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy",
  "Ivory Coast","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Korea, North","Korea, South","Kosovo",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius",
  "Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Macedonia","Norway","Oman",
  "Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal",
  "Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent","Samoa","San Marino","Sao Tome and Principe",
  "Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia",
  "South Africa","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan",
  "Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan",
  "Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City",
  "Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

// Map a few input-country names to the topojson "properties.name" used by world-atlas/countries-110m.json
export const COUNTRY_NAME_ALIASES = {
  "United States": "United States of America",
  "Korea, South": "South Korea",
  "Korea, North": "North Korea",
  "Ivory Coast": "Côte d'Ivoire",
  "Czech Republic": "Czechia",
  "Eswatini": "eSwatini",
  "Cape Verde": "Cabo Verde",
  "Timor-Leste": "East Timor",
  "Sao Tome and Principe": "São Tomé and Principe",
  "Bahamas": "The Bahamas",
  "Tanzania": "United Republic of Tanzania",
  "Serbia": "Republic of Serbia",
  "North Macedonia": "Macedonia",
  "Republic of the Congo": "Republic of Congo",
  "Congo": "Republic of Congo",
};
